// Copyright 2024 Sorama B.V.

import { autoinject, bindable, bindingMode, computedFrom, Disposable } from "aurelia-framework";
import { Point3D } from "datatypes/SoramaLocalApi";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { Point3DExtension } from "extensions/Point3DExtension";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";

@autoinject()
export class MicSelection {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedMic: Point3D;

    @bindable
    public measurements: GenericMeasurement[];

    @bindable
    public onContinue;

    @computedFrom("selectedMic")
    public get isStartAnalysisButtonEnabled(): boolean {
        return this.selectedMic !== undefined;
    }

    protected channelSelection: ChannelSelection;
    protected sensorPositions: Point3D[];
    protected mutedMicrophones: Point3D[];

    private initialSelection: boolean[];
    private channelSelectionObserver: Disposable;
    private activatePromise: Promise<void> = Promise.resolve();
    private icon = "/resources/images/icons/Microphone_front_back_view.svg";

    constructor() {
        // Pre-initialize the channel selection
        this.channelSelection = new ChannelSelection([], true);
    }

    public async attached() {
        // Optimize by checking if we already have the positions in cache
        if (this.measurements?.[0]) {
            const measurement = this.measurements[0];
            const positionsHash = MeasurementExtensions.getMicrophonePositionsHash(measurement);

            // If positions are cached, we can initialize immediately
            if (MeasurementExtensions.micPositionsCache.has(positionsHash)) {
                this.initializeWithCachedPositions(measurement);
            } else {
                // If not cached, fall back to async initialization
                await this.measurementsChanged();
            }
        }
    }

    private initializeWithCachedPositions(measurement: GenericMeasurement): void {
        this.sensorPositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(measurement);
        this.initialSelection = this.sensorPositions.map((pos) => Point3DExtension.Equals(pos, this.selectedMic));

        if (this.initialSelection.every((selected) => !selected)) {
            this.selectedMic = undefined;
        }

        this.disposeChannelSelectionObserver();
        this.channelSelection = new ChannelSelection([...this.initialSelection], true);
        this.channelSelectionObserver = this.channelSelection.changeObserver(this.onChannelSelectionChanged);
    }

    public detached() {
        this.disposeChannelSelectionObserver();
    }

    public continue() {
        this.onContinue();
    }

    private disposeChannelSelectionObserver(): void {
        if (this.channelSelectionObserver) {
            this.channelSelectionObserver.dispose();
        }
    }

    /**
     * Handles changes to selected measurements by fetching and setting microphone positions and updating observers.
     */
    private async measurementsChanged() {
        try {
            //Before we get the microphone positions for the selected measurement(s), make sure to dispose of the observer
            this.disposeChannelSelectionObserver();
            if (!this.measurements || this.measurements.length === 0) {
                return;
            }
            await this.getMeasurementMicrophonePositions(this.measurements[0]);
        } catch (error) {
            console.error("Error handling selected measurement: ", error);
        }
    }

    /**
     * Fetches and sets microphone positions for a given measurement, initializes channel selection
     * @param measurement - The measurement to fetch microphone positions for.
     */
    private async getMeasurementMicrophonePositions(measurement: GenericMeasurement): Promise<void> {
        try {
            // Restore old state
            this.sensorPositions = MeasurementExtensions.getMicrophonePositionsForMeasurement(measurement);
            // measurement.mutedSensors assumed to be indexes or identifiable sensors
            this.mutedMicrophones = measurement.mutedSensors ?? [];

            //the initial selection should exclude muted microphones
            this.initialSelection = this.sensorPositions.map((pos, index) => {
                if (this.isMicrophoneMuted(index)) {
                    return false;
                }
                return Point3DExtension.Equals(pos, this.selectedMic);
            });

            if (this.initialSelection.every((selected) => !selected)) {
                this.selectedMic = undefined;
            }

            const singleSelect = true;
            this.channelSelection = new ChannelSelection([...this.initialSelection], singleSelect);

            this.channelSelectionObserver = this.channelSelection.changeObserver(this.onChannelSelectionChanged);
        } catch (error) {
            console.error("Error fetching microphone positions: ", error);
        }
    }

    /**
     * Handles changes to selected microphone by updating the microphone index positions.
     */
    private onChannelSelectionChanged = (index?: number): void => {
        this.activatePromise = this.activatePromise.then(() => {
            const micsIndexes = this.channelSelection.getChannelSelectionIndexes(this.channelSelection);
            micsIndexes.forEach((i) => {
                if (this.isMicrophoneMuted(i)) {
                    this.channelSelection.deselect(i);
                }
            });
            const valid = this.channelSelection
                .getChannelSelectionIndexes(this.channelSelection)
                .filter((i) => !this.isMicrophoneMuted(i));

            this.selectedMic = valid.length === 1 ? this.sensorPositions[valid[0]] : undefined;
        });
    };
    private isMicrophoneMuted(index: number): boolean {
        const mic = this.sensorPositions[index];
        if (!mic || !this.mutedMicrophones) {
            return false;
        }
        return this.mutedMicrophones.some((muted) => {
            Point3DExtension.Equals(mic, muted);
        });
    }
}
